/**
 * Boton de agregar al carro (CHK-01).
 *
 * Es el mismo componente en la tarjeta del listado y en la ficha: cambia el
 * tamano, no el comportamiento. Duplicarlo para cambiarle la clase habria
 * duplicado tambien la confirmacion y el caso del producto agotado.
 *
 * Un producto sin stock no se puede agregar: se deriva a WhatsApp, que es donde
 * hoy se resuelve una reposicion (WSP-01). Ese caso lo resuelve quien llama,
 * porque el mensaje de WhatsApp necesita la URL de la ficha.
 *
 * Al agregar, el boton confirma en su propio texto durante unos segundos. La
 * confirmacion va en un `role="status"`: quien navega con lector de pantalla no
 * ve el globo de la cabecera cambiar de numero y se quedaria sin saber si el
 * clic hizo algo.
 */
import { useEffect, useRef, useState } from 'react';

import type { ProductoResumen } from '@bandu/compartido';

import { useCarro } from '../../contexto/carro-contexto';
import { nombreConVariante } from '../../utilidades/producto';
import { Icono } from '../comunes/Icono';

/** Cuanto dura la confirmacion antes de volver al texto normal. */
const MS_CONFIRMACION = 2200;

export interface BotonAgregarProps {
  producto: ProductoResumen;
  cantidad?: number;
  /** `lg` en la ficha, normal en las tarjetas del listado. */
  tamano?: 'sm' | 'normal' | 'lg';
  bloque?: boolean;
}

export function BotonAgregar({
  producto,
  cantidad = 1,
  tamano = 'normal',
  bloque = false,
}: BotonAgregarProps) {
  const { agregar } = useCarro();
  const [confirmado, setConfirmado] = useState(false);
  const temporizador = useRef<number>(undefined);

  useEffect(() => {
    return () => {
      window.clearTimeout(temporizador.current);
    };
  }, []);

  function alAgregar(): void {
    agregar(producto, cantidad);
    setConfirmado(true);
    window.clearTimeout(temporizador.current);
    temporizador.current = window.setTimeout(() => setConfirmado(false), MS_CONFIRMACION);
  }

  const clases = [
    'btn',
    tamano === 'sm' ? 'btn-sm' : '',
    tamano === 'lg' ? 'btn-lg' : '',
    confirmado ? 'btn-suave' : 'btn-primario',
    bloque ? 'btn-bloque' : '',
  ]
    .filter((clase) => clase.length > 0)
    .join(' ');

  return (
    <>
      <button type="button" className={clases} onClick={alAgregar}>
        <Icono nombre={confirmado ? 'check' : 'carro'} />
        {confirmado ? 'Agregado' : 'Agregar al carro'}
      </button>
      <span className="solo-lectores" role="status">
        {confirmado ? `${nombreConVariante(producto)} agregado al carro` : ''}
      </span>
    </>
  );
}
